/**
 * Consolidated hook for dashboard metrics.
 * Fetches all user metrics needed for the premium dashboard.
 * 
 * NOTE: Reputation data (completedNegotiations, averageRating, totalRatings) is NOT
 * fetched here — use useCurrentUserReputation for that. This avoids duplicate queries.
 * 
 * Uses localStorage cache for instant hydration on page load.
 */

import { startOfDayBrazilISO, firstDayOfMonthBrazil, lastDayOfMonthBrazil } from '@/lib/brazilTime';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { formatTimeAgo } from '@/lib/formatDate';

interface LastActivity {
  type: 'note' | 'task';
  title: string;
  timeAgo: string;
}

export interface DashboardMetrics {
  // Modo Forja
  sessionsToday: number;
  totalMinutesToday: number;
  
  // Minha Forja
  notesCount: number;
  tasksCount: number;
  storageUsedMb: number;
  storageLimitMb: number;
  storagePercentage: number;
  lastActivity: LastActivity | null;
  
  // TikTok Earnings
  tiktokMonthlyEarnings: number;
  
  // Partners
  partnerMemberships: number;
  partnerNewAnnouncements: number;
  activeCampaigns: number;
}

const defaultMetrics: DashboardMetrics = {
  sessionsToday: 0,
  totalMinutesToday: 0,
  notesCount: 0,
  tasksCount: 0,
  storageUsedMb: 0,
  storageLimitMb: 10,
  storagePercentage: 0,
  lastActivity: null,
  tiktokMonthlyEarnings: 0,
  partnerMemberships: 0,
  partnerNewAnnouncements: 0,
  activeCampaigns: 0,
};

// ============================================
// PERSISTENT CACHE FOR INSTANT HYDRATION
// ============================================
const DASHBOARD_CACHE_KEY = 'forja:dashboard-metrics:v2';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function getCachedMetrics(userId: string): DashboardMetrics | null {
  try {
    const cached = localStorage.getItem(`${DASHBOARD_CACHE_KEY}:${userId}`);
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp < CACHE_TTL_MS) {
      return data as DashboardMetrics;
    }
    return null;
  } catch {
    return null;
  }
}

function setCachedMetrics(userId: string, data: DashboardMetrics): void {
  try {
    localStorage.setItem(
      `${DASHBOARD_CACHE_KEY}:${userId}`,
      JSON.stringify({ data, timestamp: Date.now() })
    );
  } catch {
    // Ignore storage errors
  }
}


async function fetchDashboardMetrics(userId: string): Promise<DashboardMetrics> {
  const todayISO = startOfDayBrazilISO();
  
  const sevenDaysAgo = new Date(new Date(todayISO).getTime() - 7 * 86400000);
  const sevenDaysAgoISO = sevenDaysAgo.toISOString();
  
  // First and last day of current month for TikTok earnings (Brazil timezone)
  const firstDayOfMonth = firstDayOfMonthBrazil();
  const lastDayOfMonth = lastDayOfMonthBrazil();
  
  // Parallel fetch all metrics (reputation removed — use useCurrentUserReputation)
  const [
    sessionsResult,
    notesResult,
    tasksResult,
    storageResult,
    partnerMembershipsResult,
    partnerAnnouncementsResult,
    lastNoteResult,
    lastTaskResult,
    tiktokEarningsResult,
  ] = await Promise.all([
    // Modo Forja sessions today
    supabase
      .from('modoforja_sessions')
      .select('duration_seconds')
      .eq('user_id', userId)
      .gte('started_at', todayISO),
    
    // Notes count
    supabase
      .from('forja_notes')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId),
    
    // Tasks count (cards across all workspaces)
    supabase
      .from('forja_tasks_cards')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId),
    
    // Storage usage
    supabase.rpc('get_user_storage_usage', { p_user_id: userId }),
    
    // Partner memberships (approved)
    supabase
      .from('forja_partner_members')
      .select('partner_id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('status', 'approved'),
    
    // Partner announcements (last 7 days from partners user is member of)
    supabase
      .from('forja_partner_members')
      .select('partner_id')
      .eq('user_id', userId)
      .eq('status', 'approved'),
    
    // Last edited note
    supabase
      .from('forja_notes')
      .select('title, updated_at')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle(),
    
    // Last edited task
    supabase
      .from('forja_tasks_cards')
      .select('title, updated_at')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle(),
    
    // TikTok earnings for current month
    supabase
      .from('forja_tiktok_earnings')
      .select('amount')
      .eq('user_id', userId)
      .gte('earning_date', firstDayOfMonth)
      .lte('earning_date', lastDayOfMonth),
  ]);

  // Parse sessions
  const sessionsToday = sessionsResult.data?.length ?? 0;
  const totalMinutesToday = Math.round(
    (sessionsResult.data?.reduce((acc, s) => acc + (s.duration_seconds || 0), 0) ?? 0) / 60
  );

  // Parse storage
  let storageUsedMb = 0;
  let storageLimitMb = 10;
  let storagePercentage = 0;
  
  if (storageResult.data) {
    const parsed = typeof storageResult.data === 'string' 
      ? JSON.parse(storageResult.data) 
      : storageResult.data;
    storageUsedMb = parsed.used_mb || 0;
    storageLimitMb = parsed.limit_mb || 10;
    storagePercentage = parsed.percentage_used || 0;
  }

  // Parse TikTok earnings (sum all amounts - they're already in BRL)
  const tiktokMonthlyEarnings = tiktokEarningsResult.data?.reduce(
    (sum, e) => sum + (e.amount || 0),
    0
  ) ?? 0;

  // Determine last activity (most recent between note and task)
  let lastActivity: LastActivity | null = null;
  
  const lastNote = lastNoteResult.data;
  const lastTask = lastTaskResult.data;
  
  if (lastNote && lastTask) {
    const noteDate = new Date(lastNote.updated_at);
    const taskDate = new Date(lastTask.updated_at);
    if (noteDate > taskDate) {
      lastActivity = {
        type: 'note',
        title: lastNote.title || 'Sem título',
        timeAgo: formatTimeAgo(lastNote.updated_at),
      };
    } else {
      lastActivity = {
        type: 'task',
        title: lastTask.title,
        timeAgo: formatTimeAgo(lastTask.updated_at),
      };
    }
  } else if (lastNote) {
    lastActivity = {
      type: 'note',
      title: lastNote.title || 'Sem título',
      timeAgo: formatTimeAgo(lastNote.updated_at),
    };
  } else if (lastTask) {
    lastActivity = {
      type: 'task',
      title: lastTask.title,
      timeAgo: formatTimeAgo(lastTask.updated_at),
    };
  }

  // Count new announcements and active campaigns from user's partners
  let partnerNewAnnouncements = 0;
  let activeCampaigns = 0;
  
  if (partnerAnnouncementsResult.data && partnerAnnouncementsResult.data.length > 0) {
    const partnerIds = partnerAnnouncementsResult.data.map(m => m.partner_id);
    
    // New announcements (last 7 days)
    const { count: announcementsCount } = await supabase
      .from('forja_partner_announcements')
      .select('id', { count: 'exact', head: true })
      .in('partner_id', partnerIds)
      .gte('created_at', sevenDaysAgoISO);
    partnerNewAnnouncements = announcementsCount ?? 0;
    
    // Active campaigns
    const { count: campaignsCount } = await supabase
      .from('forja_partner_campaigns')
      .select('id', { count: 'exact', head: true })
      .in('partner_id', partnerIds)
      .eq('status', 'active');
    activeCampaigns = campaignsCount ?? 0;
  }

  return {
    sessionsToday,
    totalMinutesToday,
    notesCount: notesResult.count ?? 0,
    tasksCount: tasksResult.count ?? 0,
    storageUsedMb,
    storageLimitMb,
    storagePercentage,
    lastActivity,
    tiktokMonthlyEarnings,
    partnerMemberships: partnerMembershipsResult.count ?? 0,
    partnerNewAnnouncements,
    activeCampaigns,
  };
}

export function useDashboardMetrics() {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ['dashboard-metrics', user?.id],
    queryFn: async () => {
      const data = await fetchDashboardMetrics(user!.id);
      // Update cache after successful fetch
      setCachedMetrics(user!.id, data);
      return data;
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    // Instant hydration from cache
    initialData: () => {
      if (!user?.id) return undefined;
      return getCachedMetrics(user.id) ?? undefined;
    },
    // Mark as stale immediately if from cache to trigger background refresh
    initialDataUpdatedAt: () => {
      if (!user?.id) return undefined;
      const cached = localStorage.getItem(`${DASHBOARD_CACHE_KEY}:${user.id}`);
      if (cached) {
        try {
          const { timestamp } = JSON.parse(cached);
          return timestamp;
        } catch {
          return undefined;
        }
      }
      return undefined;
    },
  });

  // Never show loading if we have cached data
  const hasCachedData = user?.id ? getCachedMetrics(user.id) !== null : false;

  return {
    metrics: query.data ?? defaultMetrics,
    isLoading: query.isLoading && !hasCachedData,
    isRefetching: query.isFetching && !!query.data,
    error: query.error,
    refetch: query.refetch,
  };
}
